import type { IconType } from "@memorang/ui/icon";

export type LoadingState = {
	key: "accessCode" | "examDetails" | "exams" | "session";
	isLoading: boolean;
};

export type ExternalAuthFlowSnapshot = {
	hasAuthToken: boolean;
	hasEmail: boolean;
	hasExamId: boolean;
	hasExamDetails: boolean;
	isAccessRestricted: boolean;
	creatingSession: boolean;
	isLoadingSession: boolean;
	isErrorSession: boolean;
	isLoadingApplyAccessCode: boolean;
	isErrorApplyAccessCode: boolean;
	isLoadingExamDetails: boolean;
	isLoadingExams: boolean;
	isExamsSuccess: boolean;
	isErrorExams: boolean;
};

export const getLoadingMessages = (
	states: LoadingState[],
): { text: string; icon: IconType }[] => {
	const messageMap: Record<
		LoadingState["key"],
		{ text: string; icon: IconType }
	> = {
		accessCode: { text: "Activating your access", icon: "lock-open-outline" },
		examDetails: {
			text: "Fetching exam information",
			icon: "clipboard-outline",
		},
		exams: { text: "Loading practice tests", icon: "book-open-outline" },
		session: { text: "Securely logging you in", icon: "shield-check-outline" },
	};

	return states
		.filter((state) => state.isLoading)
		.map((state) => messageMap[state.key]);
};

export const deriveExternalAuthFlowStatus = (
	snapshot: ExternalAuthFlowSnapshot,
) => {
	const {
		hasAuthToken,
		hasEmail,
		hasExamId,
		hasExamDetails,
		isAccessRestricted,
		creatingSession,
		isLoadingSession,
		isErrorSession,
		isLoadingApplyAccessCode,
		isErrorApplyAccessCode,
		isLoadingExamDetails,
		isLoadingExams,
		isExamsSuccess,
		isErrorExams,
	} = snapshot;

	const isWaitingForAccessCodeQuery =
		hasEmail && !hasExamId && !isAccessRestricted && !isErrorApplyAccessCode;
	const isWaitingForExamDetailsQuery = hasExamId && !hasExamDetails;
	const isWaitingForExamsQuery =
		hasExamDetails && !isExamsSuccess && !isAccessRestricted && !isErrorExams;

	const loadingStates: LoadingState[] = [
		{
			key: "session",
			isLoading:
				isLoadingSession || (hasAuthToken && !hasEmail && !isErrorSession),
		},
		{
			key: "accessCode",
			isLoading: isLoadingApplyAccessCode || isWaitingForAccessCodeQuery,
		},
		{
			key: "examDetails",
			isLoading: isLoadingExamDetails || isWaitingForExamDetailsQuery,
		},
		{
			key: "exams",
			isLoading: isLoadingExams || isWaitingForExamsQuery || creatingSession,
		},
	];

	const isLoading =
		hasAuthToken &&
		(isLoadingApplyAccessCode ||
			isLoadingExamDetails ||
			isLoadingExams ||
			isLoadingSession ||
			isWaitingForAccessCodeQuery ||
			isWaitingForExamDetailsQuery ||
			isWaitingForExamsQuery ||
			creatingSession);

	const showError =
		hasAuthToken && (isErrorApplyAccessCode || isErrorSession || isErrorExams);

	return {
		messages: getLoadingMessages(loadingStates),
		isLoading,
		showError,
	};
};
